/**
 * Mailbox-credential service (MSAL).
 *
 * MSAL is no longer the app's identity — Clerk is (see deviceToken.ts). It is
 * used only for the one-time "Connect mailbox" step: acquiring a Microsoft
 * identity token (audience api://<clientId>/access_as_user) that the backend
 * exchanges via OBO for a stored Graph credential.
 *
 * The interactive step runs through an Office dialog (msalConnectDialog.ts /
 * msalDialogHandler.ts) wherever the dialog API exists; the MSAL popup remains
 * only as a fallback for non-Office contexts (e.g. the taskpane opened
 * directly in a browser during development). Popups are unreliable in Office
 * hosts: a COOP header anywhere in a federated sign-in chain severs the popup
 * handle and MSAL misreports it as `user_cancelled`.
 */
import {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  PopupRequest,
  PublicClientApplication,
} from '@azure/msal-browser';
import { captureException } from '@sentry/react';

import { backendApiRequest, loginRequest, msalConfig } from '@/config/auth/msalConfig';
import type { MailboxCredentialResult } from '@/config/auth/msalConnectDialog';
import { USER_NAME_LOCAL_STORAGE_KEY } from '@/utils/constants';
import { TimeoutError, withTimeout } from '@/utils/promise';

/**
 * How long a taskpane caller waits for the MSAL instance before giving up.
 *
 * `initialize()` has been seen never to settle on some machines, which used to
 * hang whatever awaited it with no error and no telemetry. Failing loudly is
 * always better here: every caller below has a user waiting on a button.
 *
 * Deliberately not applied inside the dialog surfaces — there a half-finished
 * redirect must be allowed to run to completion, not abandoned partway.
 */
const MSAL_INSTANCE_TIMEOUT_MS = 10_000;

// State managed through closures. The promise is memoised, not the instance,
// so concurrent callers share one initialization.
let msalInstance: Promise<IPublicClientApplication> | null = null;

/** Drops the memo, but only if it still holds `pending` — never a newer one. */
const forgetMsalInstance = (pending: Promise<IPublicClientApplication>): void => {
  if (msalInstance === pending) {
    msalInstance = null;
  }
};

/**
 * Initializes and returns the MSAL instance (lazy loading).
 *
 * Single-flight: concurrent callers share one in-flight initialization instead
 * of each constructing their own `PublicClientApplication`, which would race
 * over MSAL's shared localStorage cache and interaction state.
 *
 * A rejection clears the memo so the next caller starts over. A call that never
 * settles is bounded by the taskpane caller's timeout, which also clears it —
 * see {@link getTaskpaneMsalInstance}.
 */
export const getMsalInstance = (): Promise<IPublicClientApplication> => {
  if (!msalInstance) {
    const pending = (async () => {
      const pca = new PublicClientApplication(msalConfig);
      await pca.initialize();
      return pca;
    })();
    msalInstance = pending;
    pending.catch(() => forgetMsalInstance(pending));
  }
  return msalInstance;
};

/**
 * {@link getMsalInstance} for a taskpane caller: bounded by
 * {@link MSAL_INSTANCE_TIMEOUT_MS}, and a stall frees the memo.
 *
 * Without the reset, one `initialize()` that never settles would be shared by
 * every later caller, and each click would time out against the same dead
 * promise until the pane was reloaded. The abandoned instance is simply
 * dropped: if it settles late, nothing holds it, so it cannot replace the
 * instance a retry built.
 */
const getTaskpaneMsalInstance = async (): Promise<IPublicClientApplication> => {
  const pending = getMsalInstance();
  try {
    return await withTimeout(pending, MSAL_INSTANCE_TIMEOUT_MS, 'MSAL instance');
  } catch (error) {
    if (error instanceof TimeoutError) {
      forgetMsalInstance(pending);
    }
    throw error;
  }
};

/**
 * Returns the lowercase email-like identities advertised by an MSAL account.
 * AAD UPN (account.username) is not always the SMTP address — use ID-token
 * claims as additional signals so we don't false-positive when UPN suffix and
 * mail domain differ.
 */
export const accountIdentities = (account: AccountInfo): Array<string> => {
  const claims = account.idTokenClaims as
    | { email?: string; preferred_username?: string }
    | undefined;
  return [account.username, claims?.email, claims?.preferred_username]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map((s) => s.toLowerCase());
};

export const accountMatchesEmail = (account: AccountInfo, email: string): boolean => {
  const target = email.toLowerCase();
  return accountIdentities(account).includes(target);
};

/**
 * Popup-based acquisition of the Microsoft identity token.
 *
 * Fallback only: Office hosts go through the dialog surface, which also links
 * the mailbox itself (see msalConnectDialog / msalDialogHandler). This path
 * serves contexts with no Office dialog API — the taskpane opened directly in
 * a browser during development — and the caller posts the token itself.
 */
export const acquireMsalTokenInteractive = async (
  loginHint?: string,
): Promise<MailboxCredentialResult> => {
  try {
    const pca = await getTaskpaneMsalInstance();

    // Settle any interaction a previous load left marked as in progress, or
    // loginPopup throws interaction_in_progress. The taskpane boot used to do
    // this for every load; it no longer touches MSAL at all (see main.tsx), so
    // the cleanup happens here, where an interaction is about to start. Safe
    // on this path: it runs only in hosts with no dialog API, so no Connect
    // Mailbox dialog can have an interaction of its own in flight.
    await pca.handleRedirectPromise();

    // Request Access Token for Backend AND consent for Graph scopes in one go
    // extraScopesToConsent triggers the consent prompt for Graph permissions
    const request: PopupRequest = {
      ...backendApiRequest,
      extraScopesToConsent: loginRequest.scopes,
    };

    if (loginHint) {
      request.loginHint = loginHint;
    }

    const result: AuthenticationResult = await pca.loginPopup(request);

    // Set active account
    if (result && result.account) {
      pca.setActiveAccount(result.account);
    }

    return {
      accessToken: result.accessToken || '',
      // account.username is the UPN/email of the authenticated user — always populated
      // after a successful loginPopup, unlike Office.context.mailbox.userProfile.emailAddress
      email: result.account?.username ?? null,
    };
  } catch (error) {
    console.error('Mailbox credential acquisition failed:', error);
    // This failure previously never reached Sentry, which made the federated
    // COOP incident invisible in telemetry. `user_cancelled` is deliberately
    // NOT filtered out — a COOP-severed popup handle reports exactly that code,
    // so the tag is what separates a real cancel from the failure we hunt.
    captureException(error, {
      tags: {
        errorCode: (error as { errorCode?: string } | null)?.errorCode ?? 'unknown',
        surface: 'msal-popup',
      },
    });
    throw error;
  }
};

/**
 * Forgets the cached MSAL account (the mailbox credential in this browser).
 * Called on sign-out so shared machines don't keep the previous user's
 * Microsoft account warm in the popup.
 */
export const logoutMsal = async (): Promise<void> => {
  try {
    const pca = await getTaskpaneMsalInstance();
    const accounts = pca.getAllAccounts();

    if (accounts.length > 0) {
      // Clear all accounts and tokens
      await pca.clearCache();
      localStorage.removeItem(USER_NAME_LOCAL_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to clear MSAL session:', error);
  }
};
